/**
 * splitContent calculates physical layout heights and injects CSS page break directives
 * to prevent cut-off boxes and orphan headers.
 */
export function paginateResumeDOM(rootElement, pageHeightPx = 1123) {
  if (!rootElement) return;

  // Clear previous pagination elements
  const existingBreaks = rootElement.querySelectorAll('.page-break-indicator');
  existingBreaks.forEach(el => {
    el.style.breakBefore = 'auto';
    el.style.marginTop = '';
    el.classList.remove('page-break-indicator');
  });

  const themeInjections = rootElement.querySelector('.resume-theme-injections');
  const layoutContainer = themeInjections ? themeInjections.firstElementChild : null;
  const columnsBody = layoutContainer ? layoutContainer.querySelector('.resume-columns-body') : null;

  if (columnsBody) {
    // Measure total height of all elements above the columns body
    let initialPageHeight = 0;
    if (layoutContainer) {
      const layoutChildren = Array.from(layoutContainer.children);
      const columnsBodyIndex = layoutChildren.indexOf(columnsBody);
      if (columnsBodyIndex !== -1) {
        for (let i = 0; i < columnsBodyIndex; i++) {
          const childStyle = window.getComputedStyle(layoutChildren[i]);
          const childMb = parseFloat(childStyle.marginBottom) || 0;
          initialPageHeight += (layoutChildren[i].offsetHeight || 0) + childMb;
        }
      }
    }

    const sidebarSlot = columnsBody.querySelector('.resume-sidebar-slot');
    const mainSlot = columnsBody.querySelector('.resume-main-slot');

    let maxPages = 1;
    if (sidebarSlot) {
      const sidebarPages = paginateColumn(sidebarSlot, pageHeightPx, initialPageHeight);
      maxPages = Math.max(maxPages, sidebarPages);
    }
    if (mainSlot) {
      const mainPages = paginateColumn(mainSlot, pageHeightPx, initialPageHeight);
      maxPages = Math.max(maxPages, mainPages);
    }
    return maxPages;
  } else {
    // Single column layout (run on the root element itself)
    return paginateColumn(rootElement, pageHeightPx, 0);
  }
}

function paginateColumn(columnElement, pageHeightPx, initialPageHeight = 0) {
  const GAP_HEIGHT = 16;
  
  // Resolve theme injections padding
  let topPadding = 24;
  let bottomPadding = 24;
  const themeInjections = columnElement.closest('.resume-theme-injections');
  if (themeInjections) {
    const style = window.getComputedStyle(themeInjections);
    topPadding = parseFloat(style.paddingTop) || 0;
    bottomPadding = parseFloat(style.paddingBottom) || 0;
  }

  let blocks = Array.from(columnElement.querySelectorAll('[data-render-block="true"]'));
  const isLegacy = blocks.length === 0;
  if (isLegacy) {
    blocks = Array.from(columnElement.querySelectorAll('section, header, footer'));
  }

  // If columnElement itself contains a header slot that is NOT in the blocks list, we should account for it.
  const headerEl = columnElement.querySelector('.resume-header-slot') || columnElement.querySelector('header:not([data-ats])');
  let headerHeight = 0;
  if (headerEl && !blocks.includes(headerEl)) {
    const style = window.getComputedStyle(headerEl);
    const mb = parseFloat(style.marginBottom) || 0;
    headerHeight = (headerEl.offsetHeight || 0) + mb;
  }

  let currentPage = 1;
  let currentAbsoluteY = topPadding + initialPageHeight + headerHeight;

  blocks.forEach((el) => {
    // Avoid double counting nested blocks
    const parentSelector = isLegacy ? 'section, header, footer' : '[data-render-block="true"]';
    const parentBlock = el.parentElement.closest(parentSelector);
    if (parentBlock && columnElement.contains(parentBlock)) {
      return;
    }

    const style = window.getComputedStyle(el);
    const mb = parseFloat(style.marginBottom) || 0;
    const elHeight = (el.offsetHeight || 0) + mb;

    const allowedMaxY = currentPage * pageHeightPx + (currentPage - 1) * GAP_HEIGHT - bottomPadding;
    const yEnd = currentAbsoluteY + elHeight;

    if (yEnd <= allowedMaxY) {
      // Fits on current page
      currentAbsoluteY = yEnd;
    } else {
      // Overflows! Check if block splitting is allowed (e.g. lists of bullets)
      const splitAllowedAttr = el.getAttribute('data-split-allowed');
      const canSplit = splitAllowedAttr ? (splitAllowedAttr === 'true') : true;

      if (canSplit) {
        let childItems = Array.from(el.querySelectorAll('[data-split-child="true"]'));
        if (childItems.length === 0) {
          const allChildren = Array.from(el.children);
          if (allChildren.length > 1) {
            childItems = allChildren.slice(1);
          }
        }

        // Measure block header height (everything before the first child item)
        let blockHeaderHeight = 0;
        if (childItems.length > 0) {
          const firstChildRect = childItems[0].getBoundingClientRect();
          const blockRect = el.getBoundingClientRect();
          blockHeaderHeight = Math.max(0, firstChildRect.top - blockRect.top);
        } else {
          blockHeaderHeight = elHeight;
        }

        let accumulatedChildHeight = 0;
        let splitIndex = -1;

        for (let i = 0; i < childItems.length; i++) {
          const childStyle = window.getComputedStyle(childItems[i]);
          const childMb = parseFloat(childStyle.marginBottom) || 0;
          const childHeight = (childItems[i].offsetHeight || 0) + childMb;

          const childYEnd = currentAbsoluteY + blockHeaderHeight + accumulatedChildHeight + childHeight;
          if (childYEnd > allowedMaxY) {
            splitIndex = i;
            break;
          }
          accumulatedChildHeight += childHeight;
        }

        if (splitIndex > 0) {
          // Split at splitIndex child item
          const yNormalChildStart = currentAbsoluteY + blockHeaderHeight + accumulatedChildHeight;
          const yStartNext = currentPage * pageHeightPx + currentPage * GAP_HEIGHT + topPadding;
          const pushAmount = yStartNext - yNormalChildStart;

          applyPageBreak(childItems[splitIndex], pushAmount);

          // Update tracking to next page
          currentPage++;
          currentAbsoluteY = yStartNext + (elHeight - blockHeaderHeight - accumulatedChildHeight);
        } else {
          // Pushes the entire block to next page
          const yStartNext = currentPage * pageHeightPx + currentPage * GAP_HEIGHT + topPadding;
          const pushAmount = yStartNext - currentAbsoluteY;

          applyPageBreak(el, pushAmount);

          currentPage++;
          currentAbsoluteY = yStartNext + elHeight;
        }
      } else {
        // Indivisible block. Push to next page.
        const yStartNext = currentPage * pageHeightPx + currentPage * GAP_HEIGHT + topPadding;
        const pushAmount = yStartNext - currentAbsoluteY;

        applyPageBreak(el, pushAmount);

        currentPage++;
        currentAbsoluteY = yStartNext + elHeight;
      }
    }
  });

  return currentPage;
}

function applyPageBreak(element, pushAmount = 0) {
  element.style.breakBefore = 'page';
  element.style.marginTop = pushAmount > 0 ? `${pushAmount}px` : '0px';
  element.classList.add('page-break-indicator');
}

